import classNames from 'classnames';
import { memo } from 'react';
import { Cell } from '@tanstack/react-table';
import { toDate } from 'date-fns';

import { useAccountTimezone } from '@/lib/accounts/context';
import { getFormattedDateForPeriod } from '@/lib/date';

import { formatPropertyValue, limitLength } from '../../utils/format';
import { DataTableProperty } from './types';

import tableStyle from '@/components/table/table.module.scss';

const MaxDataLength = 500;

export const getCellTitle = (cell: Cell<any, unknown>, timezone?: string): string =>
  getTitle(cell.getValue(), timezone, cell.column.columnDef.meta?.property);

export function getTitle(value: unknown, timezone?: string, property?: DataTableProperty): string {
  if (Array.isArray(value)) {
    return value.map((item) => getTitle(item)).join(', ');
  }

  if (typeof value === 'object') {
    return limitLength(JSON.stringify(value), MaxDataLength);
  }

  if (property === undefined || property?.type !== 'Date') {
    return limitLength(value?.toString() ?? '', MaxDataLength);
  }

  const title = formatPropertyValue(value, {
    field: property,
    timezone,
    maxLength: MaxDataLength,
  });

  if (property?.precision === 'week' && typeof value === 'string') {
    return getFormattedDateForPeriod(toDate(value), property.precision, timezone);
  }

  return title;
}

type CellValueProps<T> = {
  value: unknown;
  property: DataTableProperty;
  row: T;
  onClick: (event: React.MouseEvent, row: T, property: DataTableProperty, value: unknown) => void;
};

const genericMemo: <T>(component: T) => T = memo;

export const CellValue = genericMemo(function CellValue<T>(props: CellValueProps<T>) {
  const { value, property, onClick } = props;

  const timezone = useAccountTimezone();

  if (property.type === 'Array' && Array.isArray(value)) {
    return (
      <ol
        className={classNames({
          [tableStyle.clickable]: property.type !== 'Array' && onClick !== undefined,
          [tableStyle.hasLink]: property.buildLink !== undefined && value !== null,
          [tableStyle.multiValue]: true,
        })}>
        {value.map((item, index) => (
          <li key={index} className={tableStyle.item}>
            {formatPropertyValue(item, {
              field: property,
              type: property.arrayItem?.type,
              timezone,
              maxLength: 50,
            })}
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div
      className={classNames({
        [tableStyle.clickable]: onClick !== undefined,
        [tableStyle.hasLink]: property.buildLink !== undefined && value !== null,
      })}
      onClick={(event) => onClick(event, props.row, property, value)}>
      {formatPropertyValue(value, { field: property, timezone, maxLength: MaxDataLength })}
    </div>
  );
});
